/**
 * Blocklist tab - email + domain suppression list.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, Plus, RefreshCw, Search, Trash2, X,
} from 'lucide-react';

import type { ApiCall } from '../../api/interview';
import {
  type BlocklistEntry,
  listBlocklist, addToBlocklist, removeFromBlocklist,
} from '../../api/b2b';
import { Centered, ErrorBox, HeadingWithInfo, ActionWithInfo } from './_ui';


interface Props { apiCall: ApiCall; }


const SOURCES = ['unsubscribe', 'bounce', 'manual', 'competitor', 'customer', 'spam_complaint'];


const B2BTabBlocklist: React.FC<Props> = ({ apiCall }) => {
  const [entries, setEntries] = useState<BlocklistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setEntries(await listBlocklist(apiCall, {
        search: search || undefined,
        source: sourceFilter || undefined,
        limit: 500,
      }));
    } catch (e: any) {
      setError(e?.message || 'Failed to load blocklist');
    } finally {
      setLoading(false);
    }
  }, [apiCall, search, sourceFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="p-3 border-b border-slate-100 flex flex-wrap gap-2 items-center">
        <HeadingWithInfo
          className="text-xs font-medium text-slate-600 shrink-0"
          info="Emails/Domains die nie angeschrieben werden - Bounces, Unsubscribe, Wettbewerber."
        >
          Blocklist
        </HeadingWithInfo>
        <div className="flex-1 min-w-[180px] relative">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email / domain / reason"
            className="w-full pl-8 pr-2 py-1.5 text-sm border border-slate-200 rounded-md"
          />
        </div>
        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
                className="text-sm border border-slate-200 rounded-md px-2 py-1.5">
          <option value="">All sources</option>
          {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={load} className="p-1.5 border border-slate-200 rounded-md hover:bg-slate-50">
          <RefreshCw className="w-4 h-4 text-slate-600" />
        </button>
        <ActionWithInfo info="Email oder ganze Domain manuell sperren - z.B. Wettbewerber oder Kunde.">
          <button onClick={() => setAddOpen(true)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-pink-600 text-white rounded-md hover:bg-pink-700">
            <Plus className="w-4 h-4" /> Add
          </button>
        </ActionWithInfo>
      </div>

      {loading ? (
        <Centered><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></Centered>
      ) : error ? (
        <ErrorBox message={error} onRetry={load} />
      ) : entries.length === 0 ? (
        <div className="p-12 text-center text-slate-500 text-sm">
          Blocklist is empty. Replies marked as <em>unsubscribe</em> are added automatically.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">Domain</th>
              <th className="text-left px-3 py-2">Source</th>
              <th className="text-left px-3 py-2">Reason</th>
              <th className="text-left px-3 py-2">Added</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{e.email || '-'}</td>
                <td className="px-3 py-2 font-mono text-xs">{e.domain || '-'}</td>
                <td className="px-3 py-2 text-xs">
                  <span className="inline-block px-1.5 py-0.5 bg-slate-100 rounded text-[10px] uppercase tracking-wider">
                    {e.source || '-'}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-slate-600 max-w-md truncate" title={e.reason || ''}>
                  {e.reason || '-'}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">{new Date(e.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Remove ${e.email || e.domain} from blocklist?`)) return;
                      await removeFromBlocklist(apiCall, e.id);
                      await load();
                    }}
                    className="p-1 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {addOpen && (
        <AddModal
          onClose={() => setAddOpen(false)}
          onAdd={async (payload) => {
            await addToBlocklist(apiCall, payload);
            await load();
            setAddOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default B2BTabBlocklist;


const AddModal: React.FC<{
  onClose: () => void;
  onAdd: (payload: { email?: string; domain?: string; reason?: string; source?: string }) => Promise<void>;
}> = ({ onClose, onAdd }) => {
  const [email, setEmail] = useState('');
  const [domain, setDomain] = useState('');
  const [reason, setReason] = useState('');
  const [source, setSource] = useState('manual');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    setBusy(true);
    try {
      await onAdd({
        email: email.trim() || undefined,
        domain: domain.trim() || undefined,
        reason: reason.trim() || undefined,
        source,
      });
    } catch (e: any) {
      setErr(e?.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">Add to blocklist</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Email (optional)</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="person@example.com"
                   className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Domain (optional)</label>
            <input value={domain} onChange={(e) => setDomain(e.target.value)}
                   placeholder="example.com"
                   className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Source</label>
            <select value={source} onChange={(e) => setSource(e.target.value)}
                    className="w-full text-sm border border-slate-200 rounded px-2 py-1.5">
              {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Reason (optional)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      placeholder="e.g. 'competitor - we don't pitch them'"
                      className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
          </div>
          {err && <div className="text-xs text-red-600">{err}</div>}
          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50">
              Cancel
            </button>
            <button onClick={submit} disabled={busy || (!email.trim() && !domain.trim())}
                    className="flex-1 px-3 py-2 text-sm bg-pink-600 text-white rounded hover:bg-pink-700 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Add'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
